import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useMessageThreads, useThreadMessages, useCreateThread, useSendMessage, useStaff } from '@/hooks/use-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MessageSquarePlus, Send, ArrowLeft, MessageSquare, Inbox } from 'lucide-react';
import { useRoleStore } from '@/lib/store';
import { useParams } from 'react-router-dom';
import type { MessageThread } from '@shared/types';

export function MessagesPage() {
  const { threadId: urlThreadId } = useParams();
  const [activeThread, setActiveThread] = useState<string | null>(urlThreadId || null);
  const [newOpen, setNewOpen] = useState(false);

  return (
    <AppLayout container className="bg-accent/20">
      <div className="space-y-8 animate-fade-in">
        {!activeThread ? (
          <ThreadList onSelect={setActiveThread} onNew={() => setNewOpen(true)} />
        ) : (
          <ThreadView threadId={activeThread} onBack={() => setActiveThread(null)} />
        )}
      </div>
      <NewThreadDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => { setNewOpen(false); setActiveThread(id); }} />
    </AppLayout>
  );
}

function ThreadList({ onSelect, onNew }: { onSelect: (id: string) => void; onNew: () => void }) {
  const { data, isLoading } = useMessageThreads();
  const threads = data?.items || [];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Messages</h1>
          <p className="text-muted-foreground mt-1">Internal staff communication and threads.</p>
        </div>
        <Button onClick={onNew} className="bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-6 rounded-xl shadow-lg shadow-primary/20">
          <MessageSquarePlus className="h-4 w-4 mr-2" />
          New Message
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-soft">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
              <Inbox className="h-8 w-8 text-muted-foreground/50" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-1">No conversations yet</h3>
            <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
              Start a new message thread to communicate with other staff members securely.
            </p>
            <Button onClick={onNew} variant="outline" className="rounded-xl h-10 px-5 font-bold">
              <MessageSquarePlus className="h-4 w-4 mr-2" />
              Start a Conversation
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {threads.map((t: MessageThread) => (
              <button
                key={t.id}
                onClick={() => onSelect(t.id)}
                className="w-full text-left px-6 py-4 hover:bg-muted/30 transition-colors flex items-center gap-4"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 shrink-0">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-foreground truncate">{t.subject}</span>
                    {(t.unreadCount ?? 0) > 0 && (
                      <Badge className="h-5 px-1.5 text-[9px] font-bold bg-primary text-primary-foreground border-none">
                        {t.unreadCount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{t.lastMessage?.body || 'No messages yet'}</p>
                </div>
                <span className="text-[10px] text-muted-foreground/70 font-medium shrink-0">
                  {new Date(t.lastMessageAt).toLocaleDateString()}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ThreadView({ threadId, onBack }: { threadId: string; onBack: () => void }) {
  const { data, isLoading } = useThreadMessages(threadId);
  const sendMsg = useSendMessage();
  const [text, setText] = useState('');
  const userId = useRoleStore(s => s.userId);
  const messages = data?.items || [];

  const handleSend = () => {
    if (!text.trim()) return;
    sendMsg.mutate({ threadId, body: text.trim() });
    setText('');
  };

  return (
    <>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={onBack} className="h-10 w-10 rounded-xl border-border">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Thread</h1>
          <p className="text-muted-foreground mt-1">View and reply to messages.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card shadow-soft flex flex-col" style={{ height: 'calc(100vh - 16rem)' }}>
        <ScrollArea className="flex-1 p-6">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={cn("max-w-[70%]", i % 2 === 0 ? "" : "ml-auto")}>
                  <Skeleton className="h-16 w-full rounded-xl" />
                </div>
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <MessageSquare className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No messages in this thread yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map(m => (
                <div key={m.id} className={cn("max-w-[75%]", m.senderId === userId ? "ml-auto" : "")}>
                  <div className={cn(
                    "rounded-2xl p-4",
                    m.senderId === userId
                      ? "bg-primary/10 border border-primary/20"
                      : "bg-muted/50 border border-border"
                  )}>
                    <p className="text-[10px] font-bold text-muted-foreground mb-1">{m.senderName}</p>
                    <p className="text-sm text-foreground">{m.body}</p>
                    <p className="text-[9px] text-muted-foreground/60 mt-2">{new Date(m.createdAt).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="border-t border-border p-4 flex gap-3">
          <Input
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            className="flex-1 h-11 rounded-xl bg-muted/30 border-border focus-visible:ring-primary"
          />
          <Button
            onClick={handleSend}
            disabled={!text.trim() || sendMsg.isPending}
            className="h-11 px-5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </>
  );
}

function NewThreadDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: (id: string) => void }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientId, setRecipientId] = useState('');
  const createThread = useCreateThread();
  const { data: staffData } = useStaff(open);
  const staff = staffData?.items || [];

  const handleCreate = () => {
    if (!subject.trim() || !body.trim() || !recipientId) return;
    createThread.mutate({ subject, participantIds: [recipientId], body }, {
      onSuccess: (res: any) => {
        setSubject(''); setBody(''); setRecipientId('');
        onCreated(res?.data?.threadId || '');
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>New Message</DialogTitle>
          <DialogDescription>Send a message to a staff member.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <select
            value={recipientId}
            onChange={e => setRecipientId(e.target.value)}
            className="w-full h-11 rounded-xl border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">Select recipient...</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
          </select>
          <Input placeholder="Subject" value={subject} onChange={e => setSubject(e.target.value)} className="h-11 rounded-xl" />
          <Textarea placeholder="Write your message..." value={body} onChange={e => setBody(e.target.value)} rows={4} className="rounded-xl resize-none" />
          <Button
            onClick={handleCreate}
            disabled={!subject.trim() || !body.trim() || !recipientId || createThread.isPending}
            className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 font-bold"
          >
            <Send className="h-4 w-4 mr-2" />
            Send Message
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
